
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./TrackDeployment.css";
import {
  getTrackDeploymentsList,
  getDeploymentDetails,
  cancelDeployment,
} from "../../../services/scm/TrackDeploymentAPI.js";
import Grid from "../../../components/grid/Grid";
import { formatToBrowserTime } from "../../../utils/timeFormat";
import TrackDeploymentFilter from "../../../components/filters/trackDeploymentFilter/TrackDeploymentFilter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function TrackDeployment() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({
    Succeeded: 0,
    InProgress: 0,
    Rejected: 0,
    Failed: 0,
    Queued: 0,
    Removed: 0,
    Canceled: 0,
    TimeOut: 0,
  });

  const headers = [
    { key: "jobId", label: "Deployment Name", width: "40%", sortable: true },
    { key: "status", label: "Status", width: "25%" },
    { key: "targetSelection", label: "Target Selection", width: "25%" },
    { key: "created_date", label: "Created Date", width: "30%" },
  ];

  const mapApiResponseToGridData = (dataArray) => {
    return (dataArray ?? []).map((item, index) => ({
      id: index + 1,
      jobId: item?.jobId ?? "",
      status: item?.status ?? "",
      targetSelection: item?.targetSelection ?? "",
      jobArn: item?.jobArn ?? "",
      created_date: item?.createdAt
        ? formatToBrowserTime(item.createdAt, false, true)
        : "",
    }));
  };

  async function loadData(searchData) {
    try {
      setLoading(true);
      const response = await getTrackDeploymentsList(searchData);
      const mapped = mapApiResponseToGridData(response.jobs);
      setDeployments(mapped);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  // Fetch summary from API for selected job
  async function fetchJobSummary(rows) {
    try {
      const jobIds = rows.map((row) => row.jobId);

      // Call API for all selected jobs in parallel
      const responses = await Promise.all(
        jobIds.map((id) => getDeploymentDetails({ jobId: id }))
      );

      // Initialize combined summary
      const combinedSummary = {
        Succeeded: 0,
        InProgress: 0,
        Failed: 0,
        Removed: 0,
        Queued: 0,
        Canceled: 0,
        TimeOut: 0,
        Rejected: 0,
      };

      // Merge counts from each response
      responses.forEach((response) => {
        if (response?.summary?.statusCounts) {
          const statusCounts = response.summary.statusCounts;
          combinedSummary.Succeeded += statusCounts.SUCCEEDED || 0;
          combinedSummary.InProgress += statusCounts.IN_PROGRESS || 0;
          combinedSummary.Failed += statusCounts.FAILED || 0;
          combinedSummary.Removed += statusCounts.REMOVED || 0;
          combinedSummary.Queued += statusCounts.QUEUED || 0;
          combinedSummary.Canceled += statusCounts.CANCELED || 0;
          combinedSummary.TimeOut += statusCounts.TIMED_OUT || 0;
          combinedSummary.Rejected += statusCounts.REJECTED || 0;
        }
      });

      setSummary(combinedSummary);
    } catch (error) {
      console.error("Error fetching job summaries:", error);
    }
  }

  const handleSelectionChange = useCallback(async (rows) => {
    setSelectedRows(rows);

    if (rows.length > 0) {
      await fetchJobSummary(rows);
    } else {
      // Reset summary if no rows selected
      setSummary({
        Succeeded: 0,
        InProgress: 0,
        Rejected: 0,
        Failed: 0,
        Queued: 0,
        Removed: 0,
        Canceled: 0,
        TimeOut: 0,
      });
    }
  }, []);

  const onSearch = (searchData) => {
    loadData(searchData);
  };

  const handleViewClick = () => {
    if (selectedRows.length === 1) {
      const jobId = selectedRows[0].jobId;
      navigate("/software-management/track-deployment-assets", {
        state: { jobId }
      });
    }
  };

  const handleCancelClick = async () => {
    // Keep same enable rule as button: one row & IN_PROGRESS
    if (
      selectedRows.length !== 1 ||
      selectedRows[0]?.status !== "IN_PROGRESS"
    ) {
      return;
    }

    const confirmMsg =
      selectedRows.length === 1
        ? `Are you sure you want to cancel deployment for "${selectedRows[0].jobId}"?` : "";

    const confirmed = window.confirm(confirmMsg);
    if (!confirmed) return;

    try {
      setCancelLoading(true);

      const jobId = selectedRows[0].jobId;

      const payload = {
        jobId: jobId,
        forceCancel: true,
      };

      await cancelDeployment(payload);
      toast.success(`"${jobId}" cancelled Successfully.`)
      // Reload data after cancel
      await loadData();
      setSelectedRows([]);
    } catch (err) {
      console.error("Error cancelling deployment:", err);
      toast.error("Something went wrong, Please try again.")
      setError(err);
    } finally {
      setCancelLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="container my-3 track-deployment-package h-100">
      <div className="col-12 col-xxl-10 responsive-col d-flex flex-column h-100">
        <div className="row mb-2">
          <div className="col">
            <h5 className="card-title mb-0">{t("Track Deployments")}</h5>
          </div>
        </div>

        <div className="filter-wrap">
          <TrackDeploymentFilter onSearch={onSearch} onReset={onSearch} />
        </div>

        <div className="d-flex align-items-center mb-0">
          <div className="me-auto">
            {t(`Selected Rows (${selectedRows.length})`)}
          </div>
          <div className="action-buttons">
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={
                cancelLoading || selectedRows.length !== 1 || selectedRows[0]?.status !== "IN_PROGRESS"
              }
              onClick={handleCancelClick}
              title="Cancel"
            >
              {cancelLoading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              ) : (
                <>
                <FontAwesomeIcon icon={faXmarkCircle} className="me-1" />
                Cancel
                </>
              )}
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={selectedRows.length !== 1}
              onClick={handleViewClick}
              title="View"
            >
              <FontAwesomeIcon icon={faEye} className="me-1" /> 
              View
            </button>
          </div>
        </div>

        <div className="grid-wrapper">
          <Grid
            headers={headers}
            data={deployments}
            emptyMessage={t("No Data")}
            rowsPerPage={20}
            canMultiSelect={true}
            showPagination={true}
            loading={loading}
            onSelectionChange={handleSelectionChange}
            sortable={true}
            isSummary={selectedRows.length > 0}
          />

          {/* Summary Overlay */}
          {selectedRows.length > 0 && (
            <div className="summary-footer">
              <table className="summary-table">
                <thead>
                  <tr>
                    {Object.keys(summary).map((key) => (
                      <th key={key}>{t(key)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {Object.values(summary).map((value, index) => (
                      <td key={index}>{value}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
